<template>
  <div class="media-uploader-container">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="form-label font-weight-bold m-0">{{ label }}</label>
      <span class="text-xs text-muted">亦可直接貼上圖片網址 (支援多張)</span>
    </div>

    <!-- Main Upload Area -->
    <div
      class="upload-dropzone"
      :class="{ 'is-dragging': isDragging, 'has-files': mediaItems.length > 0 }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        class="d-none"
        :accept="acceptedTypes"
        multiple
        @change="handleFileSelect"
      />

      <div class="text-center py-5">
        <div class="icon-circle mb-3 mx-auto">
          <i
            class="fas fa-cloud-upload-alt text-3xl text-primary gradient-text"
          ></i>
        </div>
        <h6 class="text-secondary font-weight-bold mb-1">點擊或拖曳檔案至此</h6>
        <p class="text-sm text-muted mb-3">
          支援 JPG, PNG, MP4 等格式 (可一次上傳多張)
        </p>
        <button type="button" class="btn bg-gradient-primary btn-sm mb-0">
          <i class="fas fa-plus me-1"></i> 選擇檔案
        </button>
      </div>
    </div>

    <!-- URL Input Expansion -->
    <div class="mt-3" v-if="!showUrlInput">
      <button
        type="button"
        class="btn btn-link btn-sm text-secondary p-0"
        @click="showUrlInput = true"
      >
        <i class="fas fa-link me-1"></i> 新增外部連結
      </button>
    </div>
    <div class="mt-2" v-else>
      <div class="input-group">
        <span class="input-group-text"><i class="fas fa-globe"></i></span>
        <input
          v-model="urlInput"
          type="text"
          class="form-control"
          placeholder="輸入圖片或影片網址..."
          @keyup.enter="addFromUrl"
        />
        <button
          class="btn btn-outline-secondary mb-0"
          type="button"
          @click="addFromUrl"
        >
          新增
        </button>
        <button
          class="btn btn-outline-danger mb-0"
          type="button"
          @click="showUrlInput = false"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Media Gallery Grid with Drag & Drop -->
    <div v-if="mediaItems.length > 0" class="mt-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="text-sm font-weight-bold mb-0 text-secondary">
          已上傳 ({{ mediaItems.length }} / {{ maxFiles }})
        </h6>
        <button
          class="btn btn-link text-danger text-xs p-0 mb-0"
          @click="clearAll"
        >
          全部清除
        </button>
      </div>

      <div class="gallery-grid" @dragover.prevent @drop.prevent>
        <div
          v-for="(item, index) in mediaItems"
          :key="item.url"
          class="gallery-item"
          draggable="true"
          @dragstart="onDragStart(index, $event)"
          @dragenter="onDragEnter(index)"
          @dragend="onDragEnd"
          @dragover.prevent
          @drop="onDrop(index)"
          :class="{
            'is-dragging-source': dragIndex === index,
            'is-drag-over': dragOverIndex === index,
          }"
        >
          <div class="media-card" :class="{ 'is-featured': index === 0 }">
            <!-- Card Content Same as before -->
            <div class="media-preview-wrapper" @click="previewImage(item)">
              <!-- Badge -->
              <span v-if="index === 0" class="featured-badge">主圖</span>
              <span v-else class="index-badge">{{ index + 1 }}</span>

              <!-- Content -->
              <div v-if="item.type === 'image'" class="media-content">
                <img :src="item.url" class="img-contain" loading="lazy" />
              </div>
              <div v-else class="media-content video-content">
                <video
                  :src="item.url"
                  :poster="item.cover_url || ''"
                  class="img-contain"
                ></video>
                <div class="play-overlay">
                  <i class="fas fa-play-circle"></i>
                </div>
              </div>

              <!-- Overlay Actions (Hover) -->
              <div class="hover-overlay">
                <div class="action-buttons">
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-white"
                    title="裁切圖片"
                    v-if="item.type === 'image'"
                    @click.stop="openCropper(index)"
                  >
                    <i class="fas fa-crop-alt text-primary"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-white"
                    title="上傳封面"
                    v-if="item.type === 'video'"
                    @click.stop="triggerCoverUpload(index)"
                  >
                    <i class="fas fa-image text-info"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-white"
                    title="移至第一張 (設為主圖)"
                    v-if="index !== 0"
                    @click.stop="moveToFirst(index)"
                  >
                    <i class="fas fa-star text-warning"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-white"
                    title="刪除"
                    @click.stop="removeItem(index)"
                  >
                    <i class="fas fa-trash text-danger"></i>
                  </button>
                </div>
                <div class="drag-handle-hint text-white mt-2 text-xs">
                  <i class="fas fa-arrows-alt me-1"></i> 按住拖曳
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add more slot -->
        <!-- ... -->
      </div>
    </div>

    <!-- Hidden Inputs -->
    <input
      ref="fileInput"
      type="file"
      class="d-none"
      :accept="acceptedTypes"
      multiple
      @change="handleFileSelect"
    />
    <input
      ref="coverInput"
      type="file"
      class="d-none"
      accept="image/*,.heic,.heif"
      @change="handleCoverSelect"
    />

    <Teleport to="body">
      <image-cropper-modal
        :show="showCropper"
        :image="cropImageSrc"
        :aspect-ratio="aspectRatio"
        @close="showCropper = false"
        @save="handleCropSave"
      />
    </Teleport>
  </div>
</template>

<script>
import request from "@/utils/request";
import Swal from "sweetalert2";

import ImageCropperModal from "./ImageCropperModal.vue";

export default {
  name: "MediaUploader",
  components: { ImageCropperModal },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "商品圖片與影片",
    },
    maxFiles: {
      type: Number,
      default: 20,
    },
    allowImages: {
      type: Boolean,
      default: true,
    },
    allowVideos: {
      type: Boolean,
      default: true,
    },
    returnObjects: {
      type: Boolean,
      default: false,
    },
    aspectRatio: {
      type: Number,
      default: NaN,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      isDragging: false,
      showUrlInput: false,
      urlInput: "",
      mediaItems: [],
      dragIndex: null,
      dragOverIndex: null,
      showCropper: false,
      cropTargetIndex: null,
      cropImageSrc: "",
      coverTargetIndex: null,
    };
  },
  computed: {
    acceptedTypes() {
      const types = [];
      if (this.allowImages) {
        types.push("image/*");
        types.push(".heic");
        types.push(".heif");
      }
      if (this.allowVideos) {
        types.push("video/*");
      }
      return types.join(",");
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (!newValue || !Array.isArray(newValue)) {
          this.mediaItems = [];
          return;
        }

        // Deep check to prevent loop
        // If we switch to objects, the prop is array of objects
        // But if simple strings, array of strings.
        // We normalize to our internal mediaItems structure.

        // If simple check:
        // const current = JSON.stringify(this.mediaItems);
        // const incoming = JSON.stringify(newValue); // Might differ in structure
        // Let's just re-map if length differs or first item differs.

        // Better: just rebuild if length or content mismatch.
        // Since we are editing in place, unnecessary rebuilds reset state (like playing video).

        const currentUrls = this.mediaItems.map((item) => item.url);
        const incomingUrls = newValue.map((v) =>
          typeof v === "string" ? v : v.url
        );

        if (
          newValue.length === this.mediaItems.length &&
          incomingUrls.every((val, index) => val === currentUrls[index])
        ) {
          // Also check covers if objects
          if (this.returnObjects) {
            const currentCovers = this.mediaItems.map(
              (item) => item.cover_url || null
            );
            const incomingCovers = newValue.map((v) => v.cover_url || null);
            if (
              incomingCovers.every((val, index) => val === currentCovers[index])
            ) {
              return;
            }
          } else {
            return;
          }
        }

        this.mediaItems = newValue
          .map((val) => this.createMediaItem(val))
          .filter((item) => item !== null);
      },
    },
    mediaItems: {
      deep: true,
      handler(newItems) {
        if (this.returnObjects) {
          // Emit full objects with url and cover_url (and maybe others if needed)
          this.$emit(
            "update:modelValue",
            newItems.map((item) => ({
              url: item.url,
              cover_url: item.cover_url || null,
              type: item.type,
            }))
          );
        } else {
          this.$emit(
            "update:modelValue",
            newItems.map((item) => item.url)
          );
        }
      },
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    triggerCoverUpload(index) {
      this.coverTargetIndex = index;
      this.$refs.coverInput.click();
    },
    async handleCoverSelect(e) {
      const file = e.target.files[0];
      e.target.value = ""; // Reset immediately
      if (!file) return;

      if (this.coverTargetIndex === null) return;

      const item = this.mediaItems[this.coverTargetIndex];
      if (!item || item.type !== "video") return;

      // Upload
      try {
        const formData = new FormData();
        formData.append("file", file);

        Swal.showLoading();
        const response = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.close();

        if (response.data.success) {
          // Update item with cover_url
          // Use splice to trigger reactivity
          const newItem = {
            ...item,
            cover_url: response.data.url,
          };
          this.mediaItems.splice(this.coverTargetIndex, 1, newItem);
          Swal.fire("成功", "封面已更新", "success");
        }
      } catch (e) {
        Swal.close();
        Swal.fire("錯誤", "上傳封面失敗", "error");
      }
    },
    // ... drop handlers ...
    handleDrop(e) {
      this.isDragging = false;
      const files = Array.from(e.dataTransfer.files);
      this.processFiles(files);
    },
    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      this.processFiles(files);
      e.target.value = ""; // Reset
    },
    async processFiles(files) {
      if (this.mediaItems.length + files.length > this.maxFiles) {
        Swal.fire("注意", `最多只能上傳 ${this.maxFiles} 個檔案`, "warning");
        return;
      }

      // Validations
      const validFiles = [];
      for (const file of files) {
        const extension = file.name.split(".").pop().toLowerCase();
        const isHeic = ["heic", "heif"].includes(extension);
        const isImage = file.type.startsWith("image/") || isHeic;
        const isVideo = file.type.startsWith("video/");

        if (!isImage && !isVideo) {
          console.warn("Skipping invalid file type", file.name, file.type);
          continue;
        }
        if (isImage && !this.allowImages) continue;
        if (isVideo && !this.allowVideos) continue;

        // 500MB Limit
        if (file.size > 500 * 1024 * 1024) {
          Swal.fire("檔案過大", `${file.name} 超過 500MB 限制`, "error");
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length === 0) return;

      const promises = validFiles.map((file) => this.uploadFile(file));

      Swal.fire({
        title: "上傳中...",
        text: `正在上傳 ${validFiles.length} 個檔案`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const results = await Promise.all(promises);
        let successCount = 0;
        results.forEach((res) => {
          if (res && res.success) {
            this.mediaItems.push({
              url: res.url,
              name: res.name,
              type: res.type,
              cover_url: null, // Init
            });
            successCount++;
          }
        });

        Swal.close();
        if (successCount < validFiles.length) {
          Swal.fire(
            "完成",
            `成功上傳 ${successCount} / ${validFiles.length} 個檔案`,
            "info"
          );
        } else {
          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          Toast.fire({
            icon: "success",
            title: "上傳成功",
          });
        }
      } catch (e) {
        console.error(e);
        const msg =
          e.response?.data?.message || e.message || "上傳過程發生錯誤";
        Swal.fire("錯誤", msg, "error");
      }
    },
    async uploadFile(file) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data.success) {
          return {
            success: true,
            url: response.data.url,
            name: file.name,
            type: file.type.startsWith("video/") ? "video" : "image",
          };
        } else {
          throw new Error(response.data.message || "Unknown error");
        }
      } catch (error) {
        console.error("Upload error for " + file.name, error);
        throw error;
      }
    },
    addFromUrl() {
      if (!this.urlInput.trim()) return;
      this.mediaItems.push(this.createMediaItem(this.urlInput.trim()));
      this.urlInput = "";
      this.showUrlInput = false;
    },
    createMediaItem(input) {
      // Determine if input is string or object
      let url = "";
      let cover = null;

      if (typeof input === "string") {
        url = input;
      } else if (input && typeof input === "object") {
        // Could be { url: '...', cover_url: '...' } OR backend provided image object
        url = input.media_url || input.url || input.image_url;
        cover = input.thumbnail_url || input.cover_url || null; // Backend uses thumbnail_url
      }

      if (!url) return null; // Should filter

      const isVideo =
        url.match(/\.(mp4|webm|mov)$/i) ||
        url.includes("youtube.com") ||
        url.includes("vimeo.com");

      return {
        url: url,
        name: "External Media",
        type: isVideo ? "video" : "image",
        cover_url: cover,
      };
    },
    removeItem(index) {
      this.mediaItems.splice(index, 1);
    },
    moveToFirst(index) {
      const item = this.mediaItems.splice(index, 1)[0];
      this.mediaItems.unshift(item);
    },
    // --- Drag & Drop Reordering ---
    onDragStart(index, event) {
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
      // Add a ghost image or styling
    },
    onDragEnter(index) {
      if (index !== this.dragIndex) {
        this.dragOverIndex = index;
      }
    },
    onDragEnd() {
      this.dragIndex = null;
      this.dragOverIndex = null;
    },
    onDrop(index) {
      if (this.dragIndex !== null && this.dragIndex !== index) {
        // Move item
        const item = this.mediaItems.splice(this.dragIndex, 1)[0];
        this.mediaItems.splice(index, 0, item);
      }
      this.dragIndex = null;
      this.dragOverIndex = null;
    },

    clearAll() {
      if (confirm("確定要清除所有圖片嗎?")) {
        this.mediaItems = [];
      }
    },
    /* eslint-disable no-console */
    previewImage() {
      // Optional: Implement lightbox or full preview
    },
    openCropper(index) {
      const item = this.mediaItems[index];
      if (!item || item.type !== "image") return;

      this.cropTargetIndex = index;
      this.cropImageSrc = item.url;
      this.showCropper = true;
    },
    async handleCropSave(file) {
      if (this.cropTargetIndex === null) return;

      // Upload the new cropped file
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.success) {
          // Update the item
          const newItem = {
            url: response.data.url,
            name: file.name,
            type: "image",
          };
          this.mediaItems.splice(this.cropTargetIndex, 1, newItem);

          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          Toast.fire({
            icon: "success",
            title: "裁切並更新成功",
          });
        }
      } catch (e) {
        console.error(e);
        Swal.fire("錯誤", "裁切後上傳失敗", "error");
      }
    },
  },
};
</script>

<style scoped>
.media-uploader-container {
  background-color: #fff;
}

/* Dropzone */
.upload-dropzone {
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.upload-dropzone:hover,
.upload-dropzone.is-dragging {
  border-color: #82d616;
  background-color: #f0fdf4;
}

.upload-dropzone.has-files {
  /* Maybe make it smaller if files exist? Or keep it big for more uploads? Keep big. */
}

.icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.gradient-text {
  background: linear-gradient(310deg, #7928ca, #ff0080);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}
@media (max-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
.gallery-item {
  cursor: grab;
  transition: transform 0.2s;
}
.gallery-item.is-dragging-source {
  opacity: 0.4;
}
.gallery-item.is-drag-over {
  transform: scale(1.05);
  border: 2px dashed #82d616;
  border-radius: 8px;
}

.gallery-item:active {
  cursor: grabbing;
}

/* Media Cards */
.media-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
  transition: all 0.2s ease;
  aspect-ratio: 1;
}

.media-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border-color: #82d616;
}

.media-card.is-featured {
  border: 2px solid #82d616;
}

.media-preview-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #f1f5f9;
}

.media-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.img-contain {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-content {
  background-color: #000;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.9);
  font-size: 2rem;
  pointer-events: none;
}

/* Badges */
.featured-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: #82d616;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.index-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 2;
}

/* Hover Overlay */
.hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.media-card:hover .hover-overlay {
  opacity: 1;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.btn-white {
  background: white;
  color: #333;
}
.btn-white:hover {
  background: #f8f9fa;
  transform: scale(1.1);
}
.btn-outline-white {
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: white;
}
.btn-outline-white:hover {
  background: white;
  color: #333;
}

.add-more-card {
  border: 2px dashed #cbd5e0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.add-more-card:hover {
  background-color: #f8f9fa;
  border-color: #a0aec0;
}
</style>
